"use client";

import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import type { PromptPhase } from "./typewriter-examples";

/**
 * Три прямоугольника-имитации сгенерированных полей под полем запроса к ИИ (t_1c710027).
 * Не настоящие поля: ввод не предусмотрен, реакция — только подсветка при наведении.
 * Подписи берём из существующих ключей типов полей (fields.types.*), новые тексты не заводим.
 */
const GHOST_FIELD_TYPES = ["text", "email", "textarea"] as const;

const GHOST_STAGGER_S = 0.18;
const GHOST_ANIMATION_S = 0.3;

interface PromptFieldGhostsProps {
	phase: PromptPhase;
}

export function PromptFieldGhosts({ phase }: PromptFieldGhostsProps) {
	const t = useTranslations("fields.types");
	const prefersReducedMotion = useReducedMotion() ?? false;
	const isVisible = phase === "fields";

	return (
		<div
			aria-hidden="true"
			className="mx-auto flex w-full max-w-3xl flex-col gap-3"
			data-slot="prompt-field-ghosts"
		>
			{GHOST_FIELD_TYPES.map((type, index) => (
				<motion.div
					animate={
						isVisible
							? { opacity: 1, scale: 1, y: 0 }
							: { opacity: 0, scale: 0.97, y: 18 }
					}
					className="rounded-xl border border-border bg-background p-3 transition-colors hover:border-primary/40 hover:bg-muted/20"
					data-slot="prompt-field-ghost"
					initial={{ opacity: 0, scale: 0.97, y: 18 }}
					key={type}
					transition={{
						delay:
							isVisible && !prefersReducedMotion ? index * GHOST_STAGGER_S : 0,
						duration: prefersReducedMotion ? 0 : GHOST_ANIMATION_S,
						ease: "easeOut",
					}}
				>
					<span className="font-medium text-muted-foreground text-xs">
						{t(`${type}.label`)}
					</span>
					<div className="mt-2 flex h-9 items-center rounded-lg border border-border/70 border-dashed bg-muted/20 px-3">
						<span className="h-1.5 w-1/3 rounded-full bg-muted-foreground/25" />
					</div>
				</motion.div>
			))}
		</div>
	);
}
